import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { EmptyState, Modal, Badge, useToast, assessmentStatusBadgeMap, SearchableSelect, InlineSpinner, TableSkeletonRows } from '../../components/UI';
import { SectionIcon } from '../../components/TrainerUI';
import { fmtDate } from '../../utils/format';

const EMPTY_FORM = { module_code: '', intake_code: '', assessment_type: 'written', title: '', due_date: '', total_students: '' };

export default function TrainerAssessmentPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [savingScores, setSavingScores] = useState(false);
  const [options, setOptions] = useState({ modules: [], intakes: [], intakeModules: [] });

  const load = () => {
    setLoading(true);
    api.get('/trainer/assessments').then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load assessments', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setError('');
    setShow(true);
    api.get('/trainer/assessments/options').then(r => setOptions(r.data.data || { modules: [], intakes: [], intakeModules: [] }))
      .catch(() => toast('Failed to load intakes/modules', 'error'));
  };

  const modulesForIntake = form.intake_code
    ? options.intakeModules.filter(im => im.intake_code === form.intake_code)
    : [];

  const moduleName = code => options.modules.find(m => m.module_code === code)?.module_name || code;

  const handleIntakeChange = intake_code => {
    setForm(f => ({ ...f, intake_code, module_code: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.intake_code) { setError('Intake is required.'); return; }
    if (!form.module_code) { setError('Module is required.'); return; }
    if (!form.assessment_type) { setError('Assessment type is required.'); return; }
    if (!form.due_date) { setError('Date is required.'); return; }
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    try {
      await api.post('/trainer/assessments', {
        ...form,
        total_students: form.total_students ? Number(form.total_students) : 0,
      });
      toast('Assessment created', 'success');
      setShow(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create assessment');
    } finally {
      setSaving(false);
    }
  };

  // Editing is really "submit for review" / "mark graded" from the trainer's
  // side — draft and returned aren't selectable targets (draft is the
  // creation default, returned is admin-only), so default to whichever of
  // the two allowed targets makes sense given the current status.
  const openEdit = row => {
    setEditing({
      id: row.id,
      title: row.title,
      currentStatus: row.status,
      remarks: row.remarks,
      status: row.status === 'graded' ? 'graded' : 'pending_review',
    });
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/trainer/assessments/${editing.id}`, {
        status: editing.status,
      });
      toast('Assessment updated', 'success');
      setEditing(null);
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openScores = row => {
    setScoring(row);
    setRosterLoading(true);
    api.get(`/trainer/assessments/${row.id}/scores`)
      .then(r => setRoster((r.data.data || []).map(s => ({ ...s, score: s.score ?? '' }))))
      .catch(() => { toast('Failed to load roster', 'error'); setScoring(null); })
      .finally(() => setRosterLoading(false));
  };

  const setRosterScore = (student_no, value) => {
    setRoster(rows => rows.map(r => r.student_no === student_no ? { ...r, score: value } : r));
  };

  const rosterAverage = () => {
    const vals = roster.map(r => r.score).filter(v => v !== '' && v !== null && v !== undefined);
    if (!vals.length) return null;
    return Math.round((vals.reduce((a, b) => a + Number(b), 0) / vals.length) * 10) / 10;
  };

  const handleSaveScores = async () => {
    setSavingScores(true);
    try {
      await api.put(`/trainer/assessments/${scoring.id}/scores`, {
        scores: roster.map(r => ({ student_no: r.student_no, score: r.score })),
      });
      toast('Scores saved', 'success');
      setScoring(null);
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save scores', 'error');
    } finally {
      setSavingScores(false);
    }
  };

  return (
    <>
      <div className="trn-topbar">
        <div className="trn-topbar-title">Assessment</div>
        <div className="trn-topbar-sub">Manage and track learner assessment results</div>
      </div>
      <div className="trn-page">
        <div className="trn-card">
          <div className="trn-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-journal-check" color="var(--sms-stat-violet)" />Assessments to Manage</h3>
            {(
              <button className="sms-btn sms-btn-primary" onClick={openCreate}>
                <i className="bi bi-plus-lg" /> New Assessment
              </button>
            )}
          </div>
          <div className="trn-card-body">
            {!loading && rows.length === 0 ? (
              <EmptyState icon="bi-journal-check" message="No assessments yet" />
            ) : (
              <table className="trn-table">
                <thead><tr><th>Module</th><th>Batch</th><th>Type</th><th>Due Date</th><th className="num">Submitted</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {loading ? <TableSkeletonRows columns={7} /> : rows.map(a => (
                    <tr key={a.id}>
                      <td>{a.module_name || a.module_code || '—'}</td>
                      <td>{a.intake_code || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{a.assessment_type}</td>
                      <td>{a.due_date ? fmtDate(a.due_date) : '—'}</td>
                      <td className="num">{a.submitted_count} / {a.total_students}</td>
                      <td>
                        <Badge value={a.status} map={assessmentStatusBadgeMap} />
                        {a.status === 'returned' && a.remarks && (
                          <i className="bi bi-info-circle" style={{ marginLeft: 6, color: 'var(--sms-txt2)', cursor: 'help' }} title={a.remarks} />
                        )}
                      </td>
                      <td className="d-flex gap-1">
                        <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => openScores(a)}>
                          <i className="bi bi-clipboard-data" /> Scores
                        </button>
                        {(
                          <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => openEdit(a)}>
                            <i className="bi bi-pencil" /> Update
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <Modal show={show} onClose={() => setShow(false)} title="New Assessment">
        <form onSubmit={handleSubmit} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake *</label>
              <SearchableSelect
                value={form.intake_code}
                placeholder="Select intake…"
                onChange={e => handleIntakeChange(e.target.value)}
                options={options.intakes.map(i => ({ value: i.intake_code, label: i.intake_code }))}
              />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module *</label>
              <SearchableSelect
                value={form.module_code}
                disabled={!form.intake_code}
                placeholder={form.intake_code ? 'Select module…' : 'Select an intake first'}
                onChange={e => {
                  const module_code = e.target.value;
                  const match = modulesForIntake.find(im => im.module_code === module_code);
                  setForm(f => ({ ...f, module_code, total_students: match ? String(match.student_count) : f.total_students }));
                }}
                options={modulesForIntake.map(im => ({
                  value: im.module_code,
                  label: moduleName(im.module_code),
                  right: `${im.student_count} enrolled`,
                }))}
              />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Assessment Type *</label>
              <select className="sms-form-control" value={form.assessment_type} onChange={e => setForm(f => ({ ...f, assessment_type: e.target.value }))}>
                <option value="written">Written</option>
                <option value="practical">Practical</option>
              </select>
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Date *</label>
              <input type="date" className="sms-form-control" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} required />
            </div>
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Title *</label>
              <input className="sms-form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Written Exam - Chapter 4" required />
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShow(false)}><i className="bi bi-x-lg" /> Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Create</>}
            </button>
          </div>
        </form>
      </Modal>

      <Modal show={!!editing} onClose={() => setEditing(null)} title="Update Assessment">
        {editing && (
          <form onSubmit={handleUpdate} noValidate>
            {editing.currentStatus === 'returned' && editing.remarks && (
              <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                <strong>Returned by admin:</strong> {editing.remarks}
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Status</label>
              <select className="sms-form-control" value={editing.status} onChange={e => setEditing(v => ({ ...v, status: e.target.value }))}>
                <option value="pending_review">Pending Review</option>
                <option value="graded">Graded</option>
              </select>
            </div>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setEditing(null)}><i className="bi bi-x-lg" /> Cancel</button>
              <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
                {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save</>}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal show={!!scoring} onClose={() => setScoring(null)} title={scoring ? `Scores — ${scoring.title || scoring.module_name || scoring.module_code || 'Assessment'}` : 'Scores'}>
        <>
            {!rosterLoading && roster.length === 0 ? (
              <EmptyState icon="bi-people" message="No active students in this batch" />
            ) : (
              <table className="trn-table">
                <thead><tr><th>Student</th><th className="num">Score</th></tr></thead>
                <tbody>
                  {rosterLoading ? <TableSkeletonRows columns={2} /> : roster.map(r => (
                    <tr key={r.student_no}>
                      <td>{r.name}</td>
                      <td className="num">
                        <input
                          type="number" min="0" max="100" className="sms-form-control"
                          style={{ width: 80, marginLeft: 'auto' }}
                          value={r.score}
                          onChange={e => setRosterScore(r.student_no, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Average: {rosterAverage() ?? '—'}</span>
              <div className="d-flex gap-2">
                <button type="button" className="sms-btn sms-btn-outline" onClick={() => setScoring(null)}><i className="bi bi-x-lg" /> Cancel</button>
                {(
                  <button type="button" className="sms-btn sms-btn-primary" disabled={savingScores || roster.length === 0} onClick={handleSaveScores}>
                    {savingScores ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save scores</>}
                  </button>
                )}
              </div>
            </div>
          </>

      </Modal>
    </>
  );
}
